import type { ProductRoadmapSectionKey } from './productRoadmapMessages';

/**
 * Product Roadmap page — route segment, CDN assets, section visuals.
 * User-facing copy lives in `productRoadmapMessages.ts` (react-intl / Transifex).
 */
export const PRODUCT_ROADMAP_2026_SEGMENT = 'product-updates/roadmap-2026';

/**
 * Author details, used for the two spots that render the name directly (not via i18n).
 * The name itself is hardcoded into each translated string (see productRoadmapMessages.ts)
 * rather than interpolated as a placeholder, so translators can pick correct grammatical
 * gender/agreement for Andrea Parodi in each language.
 */
export const PRODUCT_ROADMAP_AUTHOR = {
  firstName: 'Andrea',
  lastName: 'Parodi',
} as const;

/** Contentful CDN assets for the Product Roadmap page. */
export const PRODUCT_ROADMAP_ASSETS = {
  avatar:
    'https://images.ctfassets.net/cocwkk0wzcrd/zvV0WJySku623XIHQFkNZ/5f3481488d5cc3f2cae9ad76dcd21a3a/ap-avatar.png',
} as const;

/**
 * Each visual renders at its own aspect ratio — nothing is cropped or letterboxed, so a
 * wide hero, a square animation and a 16:9 animation can all share one renderer.
 * `width`/`height` on images reserve layout space before the file loads.
 */
export type ProductRoadmapVisual =
  | { kind: 'image'; src: string; width: number; height: number }
  | { kind: 'video'; src: string; aspectRatio: number }
  | { kind: 'placeholder'; label: string; aspectRatio: number };

/** Figma hero asset is 1824×834 — wider than the old 2:1 frame. */
export const PRODUCT_ROADMAP_HERO_ASPECT_RATIO = 1824 / 834;

/**
 * Served through Contentful's Images API at 2× the 800px column in WebP
 * (1.16 MB PNG → ~64 KB), so the hero stays sharp on retina without the payload.
 */
export const productRoadmapHeroVisual: ProductRoadmapVisual = {
  kind: 'image',
  src: 'https://images.ctfassets.net/cocwkk0wzcrd/6kdFQP6JLOemfwGreEpJCc/eafae36af3ddb3288b5bfd51b927ef26/hero-image-product-roadmap-new.png?w=1600&fm=webp&q=80',
  width: 1600,
  height: 732,
};

/** Visuals rendered after a section's body copy; sections without an entry render text only. */
/** All three animations are 1350×1080 (5:4). */
const ANIMATION_ASPECT_RATIO = 1350 / 1080;

export const productRoadmapSectionVisuals: Partial<
  Record<ProductRoadmapSectionKey, ProductRoadmapVisual>
> = {
  creatorTools: {
    kind: 'video',
    src: 'https://videos.ctfassets.net/cocwkk0wzcrd/6caWapX3XIa4IDmV7V5paT/b83477595e704166d2e39056f982a116/AutomaticPrices_ANIMATION_Compressed.mp4',
    aspectRatio: ANIMATION_ASPECT_RATIO,
  },
  reachAndDiscovery: {
    kind: 'video',
    src: 'https://videos.ctfassets.net/cocwkk0wzcrd/14lBT254cDSwLUuqPj9b0K/dd2bd27b0eb32ad48fc7d01ec002c0c8/ChatGPTl_45_ANIMATION_Compressed__1_.mp4',
    aspectRatio: ANIMATION_ASPECT_RATIO,
  },
  checkout: {
    kind: 'video',
    src: 'https://videos.ctfassets.net/cocwkk0wzcrd/grSvfqDN7PIrwaq2ZfeoL/b8af060b50e001466abec58f751811c4/AddOnsMoreVisible_ANIMATED_Compressed.mp4',
    aspectRatio: ANIMATION_ASPECT_RATIO,
  },
};

/** HeadSEO flags and canonical URL (title/description come from `productRoadmapMessages`). */
export const productRoadmap2026SeoStatic = {
  shouldIndex: true,
  shouldFollow: true,
  canonicalUrl: `https://www.eventbrite.com/${PRODUCT_ROADMAP_2026_SEGMENT}/`,
} as const;
